import {useQuery} from '@tanstack/react-query';
import {
  getCommunicationExplorerData,
  getSavedReports,
  ISavedReportResponse,
  saveCommunicationMapReport,
  postCommunicationData,
  ICommunicationDataRequest,
  ICommunicationDataResponse,
  postBulkCommunicationReport,
  IBulkCommunicationReportRequest,
  IBulkCommunicationReportResponse,
  getMetaDataFilter,
  postCommunicationBulkReport,
} from '../../api/endpoints/communicationExplorerApi';
import {
  FilterMetaDataPayload,
  ICommExplorer,
} from '../../api/types/communicationExplorer.type';
import {useMutation} from '@tanstack/react-query';
import {
  postCommunicationReport,
  CommunicationExploreView,
} from '../../api/endpoints/communicationExplorerApi';
import {ExplorerRequest} from '../../api/types/communicationExplorer.type';

export const useGetMetaDataFilter = (payload: FilterMetaDataPayload) => {
  const {sourceTypeCode, enabled} = payload;
  return useQuery<any, Error, any>({
    queryKey: ['GetMetaDataFilter', payload],
    queryFn: () => getMetaDataFilter(payload),
    enabled: !!sourceTypeCode && enabled, // don't fire until sourceTypeCode exists
  });
};

export const useCommunicationExplorer = (payload: ICommExplorer) => {
  return useQuery<any, Error, any>({
    queryKey: ['GetCommunicationExplorerData', payload],
    queryFn: () => getCommunicationExplorerData(payload),
    enabled: !!payload.reportId, // don't fire until reportId exists
  });
};
export const useGetExplorer = () => {
  return useMutation({
    mutationFn: (payload: ExplorerRequest) => CommunicationExploreView(payload),
    retry:2,
    retryDelay:1000,
  });
};

export const useSaveCommunicationMapReportData = (
  onSuccess?: (data: any) => void,
  onError?: (err: Error) => void
) =>
  useMutation<any, Error, any>({
    mutationFn: postCommunicationReport,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });

export const useSavedReports = (reportTypeCode: string) => {
  return useQuery<ISavedReportResponse, Error>({
    queryKey: ['saved-reports', reportTypeCode],
    queryFn: () => getSavedReports(reportTypeCode),
    enabled: !!reportTypeCode, // don't fire until code exists
    staleTime: 5 * 60 * 1000, // 5 mins cache
  });
};

export const useSaveCommunicationExplorerReport = (
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void
) =>
  useMutation({
    mutationFn: saveCommunicationMapReport,
    onSuccess,
    onError,
  });

export const useCommunicationData = (
  onSuccess?: (data: ICommunicationDataResponse) => void,
  onError?: (error: Error) => void
) =>
  useMutation<ICommunicationDataResponse, Error, ICommunicationDataRequest>({
    mutationFn: postCommunicationData,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });

export const useBulkCommunicationReport = (
  onSuccess?: (data: IBulkCommunicationReportResponse) => void,
  onError?: (error: Error) => void
) =>
  useMutation<
    IBulkCommunicationReportResponse,
    Error,
    IBulkCommunicationReportRequest
  >({
    mutationFn: postBulkCommunicationReport,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });

export const useCommunicationBulkReport = (
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) =>
  useMutation<
    any,
    Error,
    // Request type
    any
  >({
    mutationFn: postCommunicationBulkReport,
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
